import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

import { DerbyOption, useDerbyStore } from '@/entities/derby';
import type { DerbyQuestion } from '@/entities/derby';
import { useSessionStore } from '@/entities/session';
import { trackView, VIEW } from '@/shared/lib';
import { DerbyResultBody, Ring } from '@/features/derby-result';
import { ROUTES } from '@/shared/config';
import { RADIUS, Squircle } from '@/shared/ui';

import { useCountdown } from '../model/use-countdown';

import styles from './DerbyQuiz.module.scss';

/**
 * Прохождение Дерби (ГДД §6.2, узлы 721:5811 «вопрос» / 721:5881 «ответ» /
 * 725:6054 «итог»).
 *
 * Игрок идёт последовательно и не может вернуться назад. Экран вопроса
 * и экран ответа — разные состояния одного и того же вопроса: сервер
 * фиксирует ответ и сразу переводит `currentPosition` дальше, но пока игрок
 * не нажал «Следующий», виджет продолжает показывать РЕЗУЛЬТАТ отвеченного
 * вопроса — иначе правильность мелькнула бы на долю секунды и вопрос сразу
 * сменился. `viewedPosition` держит это состояние: пока он указывает
 * на уже отвеченный вопрос — экран ответа, как только игрок подтвердил —
 * подтягиваем то, что сервер считает текущим.
 *
 * У каждого вопроса свой дедлайн (`derby.currentDeadline`): не успел —
 * сервер сам защитывает вопрос как неверный ответ (ответ фиксируется
 * как обычно, `isCorrect: false`), клиент только перечитывает состояние
 * по истечении таймера, а не решает это сам.
 */
export function DerbyQuiz() {
  const derby = useDerbyStore((state) => state.derby);
  const isLoading = useDerbyStore((state) => state.isLoading);
  const isAnswering = useDerbyStore((state) => state.isAnswering);
  const error = useDerbyStore((state) => state.error);
  const load = useDerbyStore((state) => state.load);
  const openNext = useDerbyStore((state) => state.openNext);
  const answer = useDerbyStore((state) => state.answer);

  // Какой вопрос сейчас на экране у ИГРОКА, а не у сервера — см. комментарий
  // класса. Синхронизация — прямо в теле рендера (не в эффекте, паттерн
  // из документации React для «сбросить состояние при смене пропа»):
  // держим id последнего Дерби, под который уже подстроились, и как только
  // видим новый — сбрасываем viewedPosition тем же рендером, без лишнего
  // кадра с устаревшим экраном. Сбрасывается только при смене id, а не
  // на каждый ответ — иначе экран ответа никогда бы не показывался.
  const [viewedPosition, setViewedPosition] = useState<number | null>(null);
  const [syncedDerbyId, setSyncedDerbyId] = useState<number | null>(null);
  if (derby && derby.id !== syncedDerbyId) {
    setSyncedDerbyId(derby.id);
    setViewedPosition(
      derby.isFinished ? null : (derby.currentPosition ?? null),
    );
  }

  useEffect(() => {
    // Именно `start`, а не чтение: таймер первого вопроса начинается
    // с открытия экрана Дерби. Лента матчей читает состояние как раньше
    // и отсчёт не запускает.
    void load({ start: true });
  }, [load]);

  /**
   * Пройденное Дерби — обновить сводку игрока.
   *
   * Награда меняет не только Очки: начисление идёт через `PointsService`,
   * а следом сервер зовёт `syncProgress` — Очки месяца могли перешагнуть
   * Энкаунтер, а тот выдаёт Фанкоины или новый Множитель. Прибавить
   * `pointsAwarded` к счётчику на месте было бы дешевле, но шапка тогда
   * показывала бы новые Очки рядом со старым балансом Фанкоинов.
   * Поэтому спрашиваем сервер: он один знает всё, что изменилось.
   *
   * Только на ПЕРЕХОДЕ «не пройдено → пройдено». Игрок, открывший уже
   * пройденное Дерби, ничего не заработал, и лишний запрос ему не нужен.
   */
  const loadSummary = useSessionStore((state) => state.loadSummary);
  const wasFinished = useRef<boolean | null>(null);
  useEffect(() => {
    if (!derby) return;
    const previous = wasFinished.current;
    wasFinished.current = derby.isFinished;
    if (previous === false && derby.isFinished) {
      void loadSummary({ force: true });
    }
  }, [derby, loadSummary]);

  // ТЗ: открытия Дрибла «общее и среднее по каждой серии». Общее —
  // значит неуникальное, поэтому просмотр, а не цель; серия — в пути,
  // и разрез читается прямо в отчёте «Популярное» без настройки.
  // Срабатывает, когда Дерби загружено: до этого серии ещё не знаем
  const derbyId = derby?.id ?? null;
  useEffect(() => {
    if (derbyId === null) return;
    trackView(VIEW.derbyOpened(String(derbyId)));
  }, [derbyId]);

  if (isLoading && !derby) {
    return <p className={styles.muted}>Загружаем…</p>;
  }

  if (!derby) {
    return (
      <p className={styles.muted} data-testid="derby-empty">
        Сейчас нет доступного Дерби. Загляни позже.
      </p>
    );
  }

  // Вопрос, который игрок сейчас РАССМАТРИВАЕТ — уже решённый, если ждёт
  // «Следующий» после ответа или после истечения дедлайна (см. класс-
  // комментарий). Решённость проверяем по `isCorrect`, а не по
  // `chosenOption`: у вопроса, защитанного по таймауту, выбора нет вовсе.
  const viewedQuestion =
    viewedPosition === null
      ? undefined
      : derby.questions.find((q) => q.position === viewedPosition);

  if (viewedQuestion?.isCorrect !== undefined) {
    return (
      <AnswerFeedback
        question={viewedQuestion}
        pointsSoFar={derby.pointsSoFar}
        // Дедлайна тут нет и быть не должно: следующий вопрос ещё
        // не открыт, его таймер запустит кнопка ниже. Пропс оставлен
        // ради единственного случая — состояние восстановлено после
        // перезагрузки страницы, когда вопрос уже был открыт
        deadline={derby.currentDeadline}
        limitSeconds={derby.currentTimeLimitSeconds}
        onExpire={() => {
          void load({ force: true });
        }}
        onNext={() => {
          // Дерби пройдено — открывать нечего, и просить сервер незачем:
          // впереди только экран итога
          if (derby.isFinished) {
            setViewedPosition(null);
            return;
          }
          // Сначала сервер открывает вопрос и запускает его отсчёт,
          // и только потом показываем вопрос игроку. Наоборот значило бы
          // показать вопрос с чужим или отсутствующим таймером
          void openNext().then(() => {
            setViewedPosition(
              useDerbyStore.getState().derby?.currentPosition ?? null,
            );
          });
        }}
      />
    );
  }

  if (derby.isFinished) {
    return (
      <section className={styles.page} data-testid="derby-finished">
        <DerbyResultBody derby={derby} />

        {/* После итога — в Карьеру (правка 14.09.2026): Очки за Дерби
            двигают прогресс Главы, и игрок видит, куда они пошли */}
        <Squircle asChild cornerRadius={RADIUS.xl}>
          <Link
            className={styles.okButton}
            to={ROUTES.career}
            data-testid="derby-finished-ok"
          >
            Ок
          </Link>
        </Squircle>
      </section>
    );
  }

  const current = derby.questions.find(
    (q) => q.position === derby.currentPosition,
  );
  if (!current) return null;

  // Формулировки у вопроса нет — значит сервер его ещё не открыл: текст
  // приходит только вместе с открытием. Дойти сюда можно, если открытие
  // не прошло (сеть), и рисовать пустой вопрос с пустыми кнопками нельзя:
  // игрок жал бы наугад по невидимым вариантам.
  if (current.text === undefined) {
    return (
      <section className={styles.page} data-testid="derby-not-opened">
        <Ring color="pink">00:00:00</Ring>
        <p className={styles.muted}>
          Вопрос не открылся. Проверьте связь и попробуйте ещё раз — время на
          него ещё не пошло.
        </p>
        <Squircle asChild cornerRadius={RADIUS.xl}>
          <button
            type="button"
            className={styles.okButton}
            data-testid="derby-retry-open"
            onClick={() => {
              void openNext();
            }}
          >
            Открыть вопрос
          </button>
        </Squircle>
      </section>
    );
  }

  const handleAnswer = (option: DerbyOption): void => {
    void answer(current.id, option);
  };

  return (
    <section className={styles.page} data-testid="derby-quiz">
      {derby.currentDeadline ? (
        <TimerRing
          // Новый вопрос — новое кольцо: иначе обод «перетекал» бы
          // из пустого в полный секундной анимацией
          key={derby.currentDeadline}
          deadline={derby.currentDeadline}
          limitSeconds={derby.currentTimeLimitSeconds}
          onExpire={() => {
            // Дедлайн вопроса истёк — сервер уже защитал ответ неверным,
            // клиент только перечитывает состояние, а не решает это сам.
            void load({ force: true });
          }}
        />
      ) : (
        <Ring color="pink">{null}</Ring>
      )}

      <div className={styles.questionBlock}>
        <p className={styles.progress}>
          Вопрос {current.position} из {derby.questions.length}
        </p>
        <h2 className={styles.question}>{current.text}</h2>
      </div>

      <div className={styles.options}>
        <Squircle asChild cornerRadius={RADIUS.xl}>
          <button
            type="button"
            className={styles.option}
            disabled={isAnswering || isLoading}
            data-testid="derby-option-A"
            onClick={() => {
              handleAnswer(DerbyOption.A);
            }}
          >
            {current.optionA}
          </button>
        </Squircle>
        <Squircle asChild cornerRadius={RADIUS.xl}>
          <button
            type="button"
            className={styles.option}
            disabled={isAnswering || isLoading}
            data-testid="derby-option-B"
            onClick={() => {
              handleAnswer(DerbyOption.B);
            }}
          >
            {current.optionB}
          </button>
        </Squircle>
      </div>

      {error && (
        <p className={styles.error} data-testid="derby-error">
          {error}
        </p>
      )}
    </section>
  );
}

/**
 * Кольцо с тикающим отсчётом — вынесено отдельным компонентом, чтобы
 * раз в секунду ререндерилось только оно, не вся форма.
 *
 * Розовый обод убывает вместе со временем (правка 14.09.2026). Долю
 * считаем от времени на вопрос, которое присылает сервер, а не от
 * остатка при первом показе: после перезагрузки экрана кольцо иначе
 * начиналось бы с полного, хотя половина времени уже ушла. Сервер
 * длины не прислал — кольцо остаётся сплошным, как было.
 */
function TimerRing({
  deadline,
  limitSeconds,
  onExpire,
}: {
  deadline: string;
  limitSeconds: number | undefined;
  onExpire: () => void;
}) {
  const { text, remainingMs } = useCountdown(deadline, onExpire);
  const progress =
    limitSeconds === undefined || limitSeconds <= 0
      ? undefined
      : remainingMs / (limitSeconds * 1000);

  return (
    <Ring color="pink" {...(progress === undefined ? {} : { progress })}>
      {text}
    </Ring>
  );
}

/**
 * Экран ответа (узел 721:5881) — правильность и факт по теме вопроса,
 * один вопрос за раз.
 *
 * **Кольцо здесь тикает, и это не украшение.** Отсчёт следующего вопроса
 * на сервере идёт от момента ОТВЕТА на предыдущий (`timeline` в
 * `derby.service.ts`: `anchor = answer.answeredAt`), то есть время
 * на чтение факта вычитается из времени на следующий вопрос. Раньше
 * кольцо показывало здесь статичное «00:00:00», и игрок читал это как
 * «таймер остановлен»: 10.09.2026 пришла жалоба, где после паузы
 * на экране факта игрока перебросило с первого вопроса сразу на
 * четвёртый — два вопроса истекли, пока он читал.
 *
 * Само правило не менялось: сервер по-прежнему доматывает просроченное
 * (иначе уход на час давал бы бесплатный сброс таймера). Убрана
 * неправда в интерфейсе — теперь видно, что время идёт.
 *
 * По истечении дедлайна перечитываем состояние, а не решаем сами:
 * вопрос защитан неверным на сервере, и следующий отсчёт уже пошёл.
 */
function AnswerFeedback({
  question,
  pointsSoFar,
  deadline,
  limitSeconds,
  onExpire,
  onNext,
}: {
  question: DerbyQuestion;
  pointsSoFar: number | undefined;
  deadline: string | undefined;
  limitSeconds: number | undefined;
  onExpire: () => void;
  onNext: () => void;
}) {
  return (
    <section className={styles.page} data-testid="derby-answer-feedback">
      {deadline === undefined ? (
        // Отсчитывать нечего: Дерби пройдено либо это был последний
        // вопрос. Статичный ноль здесь — правда, а не заглушка
        <Ring color="pink">00:00:00</Ring>
      ) : (
        <TimerRing
          key={deadline}
          deadline={deadline}
          limitSeconds={limitSeconds}
          onExpire={onExpire}
        />
      )}

      <div className={styles.questionBlock}>
        <p
          className={styles.feedbackBadge}
          data-testid="derby-answer-correctness"
        >
          {/* Очки — сколько игрок набрал бы, закончись Дерби сейчас.
              Считает сервер: лесенка живёт в БД, на клиенте её нет.
              Нет числа — показываем только вердикт, а не выдуманный ноль.

              Огонёк берётся из шапки, а не из `icons/fire-fill.svg`:
              тот залит светлым (#F1F4F3) под розовые подложки модалок
              и на светлом экране ответа не виден вовсе.

              На неверном ответе Очков нет вовсе (правка 14.09.2026):
              число рядом с «Неправильно» читалось как награда за ошибку. */}
          {pointsSoFar === undefined || !question.isCorrect ? null : (
            <span
              className={styles.feedbackScore}
              data-testid="derby-answer-points"
            >
              <img
                className={styles.feedbackScoreIcon}
                src="/images/header/fire.svg"
                alt=""
                aria-hidden
              />
              {pointsSoFar}
            </span>
          )}
          {question.isCorrect ? 'Правильно' : 'Неправильно'}
        </p>
        {question.factText ? (
          <p className={styles.question}>{question.factText}</p>
        ) : null}
      </div>

      <Squircle asChild cornerRadius={RADIUS.xl}>
        <button
          type="button"
          className={styles.okButton}
          onClick={onNext}
          data-testid="derby-answer-next"
        >
          Следующий
        </button>
      </Squircle>
    </section>
  );
}
